// Native
import { Badge, Box, Button, ButtonGroup, Flex, Grid, GridItem, HStack, IconButton, Separator, Stack, Table, Tag, Text, useDisclosure } from "@chakra-ui/react";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps"
import { useParams } from "react-router"
import Moment from "react-moment"
import moment from "moment"

import { Parts, UI } from "components"
import { Styles } from "config"
import { useColorMode } from "components/ui-old/color-mode"
import useOrder from "hooks/useOrder"

export default function Order({ data }){

    const params = useParams()
    const order = useOrder(params.id ?? data?.id)
    const distributor = order.data?.distributor_from ?? {}
    const consultant_from = order.data?.consultant_from ?? {}
    const status = Styles.orderStatus(order.data?.status)
    const dialogCancel = useDisclosure()

    const tableItemsColumns = [{ children: 'Produto', width:'50%' }, { children: 'Quantidade', textAlign: 'center' }, { children: 'Und.' }, { children: 'Subtotal' }]
    const tablePaymentsColumns = [{ children: 'Método' }, { children: 'Valor' }, { children: 'Status' }, { }]

    if (distributor?.id === process.env.REACT_APP_WAREHOUSE_ID) {
        tableItemsColumns.splice(2, 0, { children: 'Quantidade Atendida', textAlign: 'center' })
    }

    const handleShare = () => {
        const url = `https://api.whatsapp.com/send?text=Ol%C3%A1%20meu%20L%C3%ADder!%20Segue%20o%20c%C3%B3digo%20do%20meu%20pedido%20de%20ades%C3%A3o%20para%20ativa%C3%A7%C3%A3o%3A%0A%0A*C%C3%B3digo%20do%20pedido%3A*%20${order.data?.cod}%0A*Link%20para%20ativa%C3%A7%C3%A3o%3A*%20https%3A%2F%2Fapp.bonifi.ca%2Fvouchers%3Fpedido%3D${order.data?.cod}`;
        window.open(url)
    }

    return(
    <>
    <Parts.Dialog.Orders.Cancel data={order.data} {...dialogCancel} />
    <Stack gap={4}>

        <Grid templateColumns={{ base: '1fr', sm: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' }} alignItems="center" gap={4}>

            <GridItem>
                <Text fontSize="lg">Pedido <b>#{order.data?.cod ?? params.id}</b></Text>
                <HStack>
                    <Badge colorPalette={status.colorPalette}>{order.data?.status_label}</Badge>
                    <Text fontSize="sm" color="GrayText"><Moment format="LLL" children={order.data?.created_at} /></Text>
                </HStack>
            </GridItem>

            <UI.Show visible={order.data?.status === 12}>
                <GridItem textAlign="right">
                    <ButtonGroup size="xs">
                        <Button colorPalette="red" variant="subtle" onClick={dialogCancel.onOpen}><UI.Icon name="cancel" /> Cancelar pedido</Button>
                    </ButtonGroup>
                </GridItem>
            </UI.Show>

        </Grid>

        <Separator borderStyle="dotted" />

        <UI.Show visible={order.data?.status === 5}>
            <UI.Alert size="sm" status="error" title="Pedido cancelado">
                Este pedido foi cancelado em <b>{moment(order.data?.canceled_at).format('LLL')}{order.data?.canceled_reason ? ` pelo motivo: ${order.data?.canceled_reason}` : '.'}</b>
            </UI.Alert>
        </UI.Show>

        <UI.Show visible={order.data?.status === 11}>
            <UI.Alert size="sm" status="success" title="Aguardando retirada">
                O seu pedido está pronto e aguardando a sua retirada no núcleo escolhido.
            </UI.Alert>
        </UI.Show>

        <HStack gap={8}>
            <Box>
                <Text fontSize="sm" color="GrayText">Total do pedido:</Text>
                <Text fontSize="lg" color="green"><UI.Number.Amount value={order.data?.total_amount} /></Text>
            </Box>
            <Box>
                <Text fontSize="sm" color="GrayText">Tipo de entrega:</Text>
                <Text>{order.data?.shipping_type_label}</Text>
            </Box>
        </HStack>

        <UI.Table label="Itens do seu pedido:" columns={tableItemsColumns} data={order.data?.itens} Row={(rowProps) => (<ItemRow {...rowProps} distributorFrom={distributor} />)} />

        <UI.Table label="Formas de pagamento:" columns={tablePaymentsColumns} data={order.data?.transactions} Row={PaymentRow} />

        <UI.Show visible={order.data?.status === 12}>
            <UI.Alert size="sm" status="warning" title="Pagamentos pendentes">
                Um ou mais formas de pagamento do seu pedido não foram confirmadas. Evite perder a reserva dos itens selecionados e o cancelamento do pedido, realize o pagamento o quanto antes.
            </UI.Alert>
        </UI.Show>

        {/* Núcleo de distribuição */}
        <UI.Show visible={!!distributor?.id}><Distributor data={{ distributor, order: order.data }} /></UI.Show>

        <UI.Show visible={!distributor?.id && order.data?.type === 3}>

            <UI.Show visible={!consultant_from?.id}>
                <Flex justifyContent="space-between" alignItems="center" p={4} borderWidth={1} borderColor="orange" borderRadius={4} borderStyle="dashed" backgroundColor="orange.subtle">
                    <HStack gap={4}>
                        <UI.Avatar icon={<UI.Icon name="voucher" />} colorPalette="orange" />
                        <Box>
                            <Text fontWeight="bold" color="orange.fg">Solicitar ativação</Text>
                            <Text fontSize="sm" color="GrayText">Fale com seu líder e peça para que ele faça a sua ativação através de voucher.</Text>
                        </Box>
                    </HStack>
                    <Box>
                        <Button colorPalette="teal" onClick={handleShare}><UI.Icon name="whatsapp" /> Compartilhar pedido</Button>
                    </Box>
                </Flex>
            </UI.Show>

        </UI.Show>

    </Stack>
    </>
    )
}

const Distributor = ({ data, ...props }) => {

    const { order, distributor } = data
    const colorMode = useColorMode()

    const handleDirections = () => window.open(`https://www.google.com/maps/dir/?api=1&destination=${distributor?.lat},${distributor?.lng}`)

    const handleWhatsapp = () => window.open(`https://wa.me/${distributor.mobile}?text=Ol%C3%A1!%20Gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20o%20pedido%20(${order.cod})%20que%20realizei%20em%20seu%20n%C3%BAcleo.`)

    return(
    <>
    <Text color="GrayText">Núcleo de distribuição:</Text>

    <Grid templateColumns={{ base: '1fr', sm: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' }} gap={4}>

        <GridItem>
            <Stack gap={4}>

                {/* Dados do núcleo */}
                <HStack>
                    <UI.Avatar name={distributor?.name} />
                    <Box>
                        <Text>{distributor?.name}</Text>
                        <Text fontSize="sm" color="GrayText">{distributor?.city?.name}, {distributor?.city?.state_code}</Text>
                    </Box>
                </HStack>

                <Separator />

                {/* Para entrega */}
                <UI.Show visible={order?.shipping_type === 1}>
                    <Box>
                        <Text fontSize="sm" color="GrayText">Endereço de entrega:</Text>
                        <Text>{order?.address_line_1} {order?.address_line_2}, {order?.address_number ?? 'S/N'}</Text>
                        <Text textTransform="capitalize">{order?.address_neighborhood}, {order?.address_zip}</Text>
                    </Box>
                    <Box>
                        <UI.Show visible={!!order?.shipping_track_code}>
                            <Text fontSize="sm" color="GrayText">Código de rastreio:</Text>
                            <Text>{order?.shipping_track_code}</Text>
                        </UI.Show>
                    </Box>
                </UI.Show>

                {/* Para retirada */}
                <UI.Show visible={order?.shipping_type === 2}>
                    <Box>
                        <Text fontSize="sm" color="GrayText">Endereço para retirada:</Text>
                        <Text>{distributor?.address_line_1} {distributor?.address_line_2}, {distributor?.address_number ?? 'S/N'}</Text>
                        <Text textTransform="capitalize" fontSize="sm">{distributor?.address_neighborhood}, {distributor?.zip}</Text>
                    </Box>
                    <ButtonGroup size="xs" variant="subtle">
                        <Button onClick={handleWhatsapp}><UI.Icon name="whatsapp" /> Whatsapp</Button>
                        <Button onClick={handleDirections}><UI.Icon name="directions" /> Como chegar</Button>
                    </ButtonGroup>
                </UI.Show>

            </Stack>
        </GridItem>

        <GridItem>
            <UI.Pressable onClick={handleDirections}>
                <APIProvider apiKey="AIzaSyD0H9SsIRjE8mdlEKtPRRWElxzRzHtAqbg">
                    <Map
                        reuseMaps
                        controlled
                        style={{width: '100%', height: '180px', borderRadius: '10%' }}
                        center={{lat: distributor?.lat ?? 1, lng: distributor?.lng ?? 1 }}
                        zoom={10}
                        gestureHandling={'greedy'}
                        disableDefaultUI={true}
                        colorScheme={colorMode.colorMode === 'dark' ? 'DARK' : 'LIGHT'}

                    >
                        <Marker
                            position={{lat: distributor?.lat, lng: distributor?.lng }}
                            clickable={false}
                        />
                    </Map>
                </APIProvider>
            </UI.Pressable>
        </GridItem>

    </Grid>
    </>
    )
}

const ItemRow = ({ data, distributorFrom = null, ...props }) => {

    const price = data.product?.consultant_price ?? data?.product_kit?.price_consultant
    const label = data.product?.name ?? data.product_kit?.label

    return(
    <Table.Row {...props}>
        <Table.Cell fontSize="xs">{label}</Table.Cell>
        <Table.Cell fontSize="xs" textAlign="center"><Tag.Root colorPalette="orange"><Tag.Label>{ data.quantity }</Tag.Label></Tag.Root></Table.Cell>
        { distributorFrom?.id === process.env.REACT_APP_WAREHOUSE_ID && <Table.Cell fontSize="xs" textAlign="center"><Tag.Root colorPalette="orange"><Tag.Label>{data?.shipped_quantity ?? data?.quantity }</Tag.Label></Tag.Root></Table.Cell> }
        <Table.Cell fontSize="xs"><UI.Number.Amount value={price} /></Table.Cell>
        <Table.Cell><UI.Number.Amount value={data.amount} /></Table.Cell>
    </Table.Row>
    )
}

const PaymentRow = ({ data, order, ...props }) => {
    const method = Styles.transactionMethod(data.method)
    const status = Styles.transactionStatus(data.status)
    const pix = useDisclosure()
    const attach = useDisclosure()

    const handleShareThird = () => {
        const url = `https://api.whatsapp.com/send?text=Ol%C3%A1%20meu%20l%C3%ADder!%20Segue%20os%20dados%20do%20meu%20pedido%20para%20realiza%C3%A7%C3%A3o%20do%20pagamento%20de%20terceiros%3A%0A%0A*C%C3%B3digo%20do%20pedido%3A*%20${order.cod}%0A*Link%20para%20pagamento%3A*%20https%3A%2F%2Fmmn.bonificaoficial.com%2Fbonus%3Fthird%3D${order.cod}`;
        window.open(url)
    }

    const handleShareVoucher = () => {
        const url = `https://api.whatsapp.com/send?text=Ol%C3%A1%20meu%20L%C3%ADder!%20Segue%20o%20c%C3%B3digo%20do%20meu%20pedido%20de%20ades%C3%A3o%20para%20ativa%C3%A7%C3%A3o%3A%0A%0A*C%C3%B3digo%20do%20pedido%3A*%20${order.data?.cod}%0A*Link%20para%20ativa%C3%A7%C3%A3o%3A*%20https%3A%2F%2Fmmn.bonificaoficial.com%2Fvouchers%3Fpedido%3D${order.data?.cod}`;
        window.open(url)
    }

    return(
    <>
    <Parts.Dialog.Transactions.Pix {...pix} data={data} />
    <Parts.Dialog.Transactions.Attach {...attach} data={data} />
    <Table.Row {...props}>
        <Table.Cell color={`${method.colorPalette}.fg`}><UI.Icon name={method.icon} /> {data.method_label}</Table.Cell>
        <Table.Cell fontSize="xs"><UI.Number.Amount value={data.amount} /></Table.Cell>
        <Table.Cell fontSize="xs"><Badge colorPalette={status.colorPalette}><UI.Icon name={status.icon} /> {data.status_label}</Badge></Table.Cell>
        <Table.Cell textAlign="end">

            {/* Mercado Pago */}
            <UI.Show visible={data.method === 5 && data.status === 3}>
                <Button size="xs" colorPalette="blue" onClick={() => window.open(data.payment_url)}><UI.Icon name="mercadopago" /> Pagar agora <UI.Icon name="next" /></Button>
            </UI.Show>

            {/* Cielo */}
            <UI.Show visible={data.method === 9 && data.status === 3}>
                <Button size="xs" colorPalette="orange" onClick={() => window.open(data.payment_url)}><UI.Icon name="cielo" /> Pagar agora <UI.Icon name="next" /></Button>
            </UI.Show>

            {/* Pix */}
            <UI.Show visible={data.method === 4 && data.status === 3}>
                <ButtonGroup>
                    <Button size="xs" colorPalette="gray" onClick={attach.onOpen}><UI.Icon name="attach" /> Anexar comprovante</Button>
                    <Button size="xs" colorPalette="gray" onClick={pix.onOpen}><UI.Icon name="pix" /> Pagar agora <UI.Icon name="next" /></Button>
                </ButtonGroup>
            </UI.Show>

            {/* Pagamento de Terceiros */}
            <UI.Show visible={data.method === 7 && data.status === 3}>
                <HStack justifyContent="flex-end">
                    <Text fontSize="xs" color="red.fg">Peça ao seu consultor parceiro para realizar o pagamento.</Text>
                    <IconButton size="xs" variant="ghost" colorPalette="green" onClick={handleShareThird}><UI.Icon name="whatsapp" /></IconButton>
                </HStack>
            </UI.Show>

            {/* Voucher */}
            <UI.Show visible={data.method === 8 && data.status === 3}>
                <HStack justifyContent="flex-end">
                    <Text fontSize="xs" color="red.fg">Solicite a ativação para um consultor.</Text>
                    <IconButton size="xs" variant="ghost" colorPalette="green" onClick={handleShareVoucher}><UI.Icon name="whatsapp" /></IconButton>
                </HStack>
            </UI.Show>
        </Table.Cell>
    </Table.Row>
    </>
    )
}
